import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Fragment, useState } from "react";
import { useFinance } from "./FinanceContext";
import { sumTo } from "./data/model";
import { CUR_MONTH_IDX, MONTHS, SEED } from "./data/seedData";
import type { BudgetItem } from "./data/types";
import { AnnualBar } from "./components/AnnualBar";

export function ActualExpensesPage() {
  const { model, fmt, openVouchers, lastChange } = useFinance();
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const query = q.trim().toLowerCase();
  const match = (it: BudgetItem) =>
    !query ||
    it.name.toLowerCase().includes(query) ||
    it.code.toLowerCase().includes(query) ||
    it.led.toLowerCase().includes(query);
  function toggle(l: string) {
    const n = new Set(collapsed);
    if (n.has(l)) n.delete(l);
    else n.add(l);
    setCollapsed(n);
  }
  const cells = lastChange.cells;
  const ck = (id: string, m: number) => Boolean(cells[id + "|actual|" + m]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Actual Expenses
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Monthly actuals imported from approved Tally uploads · read-only · click a figure to view
        its vouchers
      </Typography>
      <AnnualBar />
      <Alert severity="warning" sx={{ mb: 2 }}>
        Actuals are not manually editable — they flow in only from validated &amp; approved Tally
        imports. Future months show &quot;—&quot; until data is uploaded.
      </Alert>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search cost head…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button size="small" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Button>
        <Button
          size="small"
          onClick={() => setCollapsed(new Set(SEED.categories.map((c) => c.letter)))}
        >
          Collapse all
        </Button>
      </Stack>
      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Cost Head</TableCell>
              {MONTHS.map((m) => (
                <TableCell key={m} align="right">
                  {m}
                </TableCell>
              ))}
              <TableCell align="right">YTD Actual</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {SEED.categories.map((cat) => {
              const its = model.items.filter((i) => i.catLetter === cat.letter && match(i));
              if (query && !its.length) return null;
              const isCol = collapsed.has(cat.letter) && !query;
              return (
                <Fragment key={cat.letter}>
                  <TableRow
                    hover
                    onClick={() => toggle(cat.letter)}
                    sx={{ cursor: "pointer", bgcolor: "action.hover" }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>{cat.code}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {isCol ? "▸" : "▾"} {cat.name}{" "}
                      <Chip size="small" label={cat.type} sx={{ ml: 1 }} />
                    </TableCell>
                    {MONTHS.map((_m, i) => (
                      <TableCell key={i} align="right">
                        {i <= CUR_MONTH_IDX
                          ? fmt(its.reduce((s, it) => s + (it.actual[i] ?? 0), 0))
                          : "—"}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      {fmt(its.reduce((s, it) => s + sumTo(it.actual, CUR_MONTH_IDX), 0))}
                    </TableCell>
                  </TableRow>
                  {!isCol
                    ? its.map((it) => (
                        <TableRow key={it.id}>
                          <TableCell>{it.code}</TableCell>
                          <TableCell>{it.name}</TableCell>
                          {MONTHS.map((_m, i) =>
                            i <= CUR_MONTH_IDX ? (
                              <TableCell
                                key={i}
                                align="right"
                                sx={{
                                  cursor: "pointer",
                                  color: "primary.main",
                                  textDecoration: "underline dotted",
                                  bgcolor: ck(it.id, i) ? "action.selected" : undefined,
                                }}
                                onClick={() => openVouchers(it, "cm", i)}
                              >
                                {fmt(it.actual[i] ?? 0)}
                              </TableCell>
                            ) : (
                              <TableCell key={i} align="right" sx={{ color: "text.disabled" }}>
                                —
                              </TableCell>
                            ),
                          )}
                          <TableCell align="right">
                            {fmt(sumTo(it.actual, CUR_MONTH_IDX))}
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </Fragment>
              );
            })}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: 2, borderColor: "divider" } }}>
              <TableCell colSpan={2}>TOTAL ACTUAL</TableCell>
              {MONTHS.map((_m, i) => (
                <TableCell key={i} align="right">
                  {i <= CUR_MONTH_IDX
                    ? fmt(model.items.reduce((s, it) => s + (it.actual[i] ?? 0), 0))
                    : "—"}
                </TableCell>
              ))}
              <TableCell align="right">
                {fmt(model.items.reduce((s, it) => s + sumTo(it.actual, CUR_MONTH_IDX), 0))}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
